<template>
    <div :class="namespaceClass()">
        <mds-table row-numbers :fixed-first-col="fixedFirstCol" :class="namespaceClass('contribution-table')" @scroll.native="handleTableScrolled">
            <mds-thead>
                <mds-th v-for="(header, colIndex) in tableHeaders" :key="colIndex" :right-aligned="header.align === 'right'" :style="header.style">
                    {{ header.text }}
                </mds-th>
            </mds-thead>
            <mds-tbody>
                <mds-tr
                    type="expandable"
                    aria-controls="contributions"
                    :row-number="1"
                    :expanded="isExpandContribution"
                    total-row
                    @click.native="toggleContributions"
                    @keyup.enter.native="toggleContributions"
                >
                    <mds-td v-for="(header, colIndex) in tableHeaders" :key="colIndex" :right-aligned="header.align === 'right'" :style="header.style">
                        <template v-if="header.fieldName === 'name'">
                            {{ initContributionRow.name }}
                        </template>
                        <template v-else>
                            {{ header.transform ? header.transform(initContributionRow[header.fieldName]) : initContributionRow[header.fieldName] }}
                        </template>
                    </mds-td>
                </mds-tr>
                <mds-tr type="container" id="contributions" v-if="isExpandContribution" :class="namespaceClass('holdings-table-container')">
                    <mds-td :colspan="tableHeaders.length + 1">
                        <mds-table row-numbers>
                            <mds-thead hidden-header>
                                <mds-th
                                    v-for="(header, colIndex) in tableHeaders"
                                    :key="colIndex"
                                    :right-aligned="header.align === 'right'"
                                    :style="header.style"
                                >
                                    {{ header.text }}
                                </mds-th>
                            </mds-thead>
                            <mds-tbody>
                                <mds-tr :row-number="1">
                                    <mds-td
                                        v-for="(header, colIndex) in tableHeaders"
                                        :key="colIndex"
                                        :right-aligned="header.align === 'right'"
                                        :style="header.style"
                                    >
                                        <template v-if="header.fieldName === 'name'">
                                            <span class="holdings-header">{{ getUILabel("portfolio_transactions_holdings_row_name") }}</span>
                                        </template>
                                    </mds-td>
                                </mds-tr>
                                <mds-tr v-for="(row, rowIndex) in contributionHoldings" :key="rowIndex" :row-number="rowIndex + 2">
                                    <mds-td
                                        v-for="(header, colIndex) in tableHeaders"
                                        :key="colIndex"
                                        :right-aligned="header.align === 'right'"
                                        :style="header.style"
                                    >
                                        {{ header.transform ? header.transform(row[header.fieldName]) : row[header.fieldName] }}
                                    </mds-td>
                                </mds-tr>
                            </mds-tbody>
                        </mds-table>
                    </mds-td>
                </mds-tr>
                <mds-tr v-for="(row, rowIndex) in visibleSubSequentsRecord" :key="rowIndex" :row-number="rowIndex + 2" class="subsequents">
                    <mds-td v-for="(header, colIndex) in tableHeaders" :key="colIndex" :right-aligned="header.align === 'right'" :style="header.style">
                        <template v-if="header.fieldName === 'name'">
                            <template v-if="row.status < 1">
                                <mds-select
                                    :e2e="`transaction-type-${rowIndex}`"
                                    size="small"
                                    hidden-label
                                    :label="getUILabel('portfolio_transactions_column_type')"
                                    @change="(value, $event) => selectType($event, row)"
                                >
                                    <option
                                        v-for="item in subSequentTypes"
                                        :key="item.typeId"
                                        :selected="item.typeId === row.type"
                                        :value="item.typeId"
                                        :disabled="!!item.disabled"
                                        >{{ item.labelId ? getUILabel(item.labelId) : item.text }}</option
                                    >
                                </mds-select>
                            </template>
                            <template v-else>
                                {{ row.nameLabelId ? getUILabel(row.nameLabelId) : row.name }}
                            </template>
                        </template>
                        <template v-else-if="header.fieldName === 'frequency'">
                            <template v-if="row.status < 1">
                                <mds-select
                                    :e2e="`transaction-frequency-${rowIndex}`"
                                    size="small"
                                    hidden-label
                                    :label="getUILabel('portfolio_transactions_column_frequency')"
                                    @change="(value, $event) => selectFrequency($event, row)"
                                >
                                    <option
                                        v-for="item in frequencies"
                                        :key="item.id"
                                        :value="item.id"
                                        :selected="item.id === row.frequency"
                                        :disabled="isFrequencyDisabled(item.id, row.frequency)"
                                        >{{ item.labelId ? getUILabel(item.labelId) : item.text }}</option
                                    >
                                </mds-select>
                            </template>
                            <template v-else>{{ getFrequencyById(row.frequency) }}</template>
                        </template>
                        <template v-else-if="header.fieldName === 'unit'">
                            <template v-if="row.status < 1">
                                <mds-select
                                    :e2e="`transaction-unit-${rowIndex}`"
                                    size="small"
                                    hidden-label
                                    :label="getUILabel('portfolio_transactions_column_unit')"
                                    @change="(value, $event) => selectUnit($event, row)"
                                >
                                    <option
                                        v-for="item in portfolioAllocateByList"
                                        :key="item.id"
                                        :value="item.id"
                                        :selected="item.id === row.unit"
                                        :disabled="!!item.disabled"
                                        >{{ getUILabel(item.labelId) }}</option
                                    >
                                </mds-select>
                            </template>
                            <template v-else>{{ getUnitByAllocateBy(row.unit) }}</template>
                        </template>
                        <template v-else-if="header.fieldName === 'value'">
                            <template v-if="row.status < 1">
                                <mds-input
                                    :e2e="`transaction-amount-${rowIndex}`"
                                    size="small"
                                    :label="getUILabel('holdings_amount')"
                                    hidden-label
                                    :value="row.value ? formatter.format(row.value, 'shortDecimal') : ''"
                                    @change="e => changeValue(e, row)"
                                ></mds-input>
                            </template>
                            <template v-else>
                                {{ header.transform(row.value) }}
                            </template>
                        </template>
                        <template v-else-if="header.fieldName === 'startDate'">
                            <template v-if="row.status < 1 && row.frequency === 99">
                                <hypo-date-input
                                    :key="rowIndex + 'sd'"
                                    class="date-input"
                                    :e2e="`transaction-type-${rowIndex}`"
                                    :label="getUILabel('holdings_start_date')"
                                    size="small"
                                    dateFormat="mmyyyy_slash"
                                    :error="errorToggle && tooltipTrigger === rowIndex + 'sd'"
                                    :ref="rowIndex + 'sd'"
                                    :id="rowIndex + 'sd'"
                                    :mwc-id="rowIndex + 'sd'"
                                    :value="row && row.startDate ? new Date(row.startDate) : null"
                                    @input="e => errorToggle && changeStartDate(e, row, rowIndex)"
                                    @dateBlur="e => changeStartDate(e, row, rowIndex)"
                                ></hypo-date-input>
                            </template>
                            <template v-else>
                                {{ header.transform(row.startDate) }}
                            </template>
                        </template>
                        <template v-else-if="header.fieldName === 'endDate'">
                            {{ row.frequency === 99 ? "&mdash;" : header.transform(row.endDate) }}
                        </template>
                        <template v-else-if="header.fieldName === 'emptyCol'">
                            <mds-button
                                :e2e="`transaction-cancel-remove-${rowIndex}`"
                                variation="flat"
                                type="button"
                                size="small"
                                @click="handleFirstAction(row)"
                                >{{ row.status === 1 ? getUILabel("common_remove") : getUILabel("common_cancel") }}</mds-button
                            >
                            <mds-button
                                :e2e="`transaction-add-done-${rowIndex}`"
                                variation="flat"
                                type="button"
                                size="small"
                                :disabled="!isValidRowData(row)"
                                @click="handleSecondAction(row)"
                            >
                                {{ row.status === -1 ? getUILabel("common_add") : row.status === 0 ? getUILabel("common_done") : getUILabel("common_edit") }}
                            </mds-button>
                        </template>
                    </mds-td>
                </mds-tr>
            </mds-tbody>
        </mds-table>
        <mds-button
            e2e="add-transaction"
            :class="namespaceClass('add-transaction')"
            icon="plus"
            size="small"
            variation="secondary"
            type="button"
            :text="getUILabel('portfolio_transactions_add')"
            :disabled="subSequentsData.filter(item => item.status < 1 && item.visible).length > 0"
            @click="addTransaction"
            v-if="!transactionLimitReached"
        ></mds-button>
        <span v-else class="transaction-limit-message">{{ getUILabel("portfolio_transactions_limit_reached") }}</span>
        <mds-tooltip
            e2e="transaction-error-tooltip"
            v-if="errorToggle"
            variation="error"
            ref="errorTooltip"
            :triggered-by="tooltipTrigger"
            v-model="errorToggle"
            :position="['bottom-center']"
        >
            <span v-if="tooltipType == dateErrorStatus.invalidDate">{{ getUILabel("portfolio_transactions_tooltip_error_format") }}</span>
            <span v-if="tooltipType == dateErrorStatus.beforeStartdate">{{ getUILabel("portfolio_transactions_tooltip_error_before_startdate") }}</span>
            <span v-if="tooltipType == dateErrorStatus.afterEnddate">{{ getUILabel("portfolio_transactions_tooltip_error_after_enddate") }}</span>
            <span v-if="tooltipType == dateErrorStatus.usedDate">{{ getUILabel("portfolio_transactions_tooltip_error_used_date") }}</span>
        </mds-tooltip>
    </div>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import { generateUUID, getDateString, isValidDate, isValidTransactionRecord, checkValidDate } from "@/util.js";
import * as types from "../store/mutation-types.js";
import { MwcResponsiveMixin } from "mwc-vue-component-helper";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import MdsTooltip from "@mds/tooltip";
import MdsInput from "@mds/input";
import { MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd } from "@mds/data-table-v4";
import HypoDateInput from "hypo-date-input/src/DateInput";
import { MdsButton } from "@mds/button";
import MdsSelect from "@mds/select";
import debounce from "lodash.debounce";
import cloneDeep from "lodash.clonedeep";

export default {
    name: "portfolio-transaction",
    components: {
        MdsTooltip,
        MdsButton,
        MdsInput,
        MdsTable,
        MdsThead,
        MdsTh,
        MdsTbody,
        MdsTr,
        MdsTd,
        HypoDateInput,
        MdsSelect
    },
    mixins: [HypoVueBaseMixin, MwcSharedMixin, MwcResponsiveMixin],
    data() {
        return {
            defaultConfig: {
                labels: this.$store.state.labels.labels,
                format: {
                    "mm/yyyy": {
                        dataType: "date",
                        year: "numeric",
                        month: "2-digit"
                    }
                }
            },
            dateErrorStatus: {
                invalidDate: "INVALID_DATE",
                beforeStartdate: "BEFORE_STARTDATE",
                afterEnddate: "AFTER_ENDDATE",
                usedDate: "USED_DATE"
            },
            errorToggle: false,
            fixDateData: { secIndex: "", fixedValue: "", sec: {} },
            tooltipTrigger: "",
            tooltipType: "",
            isExpandContribution: false,
            subSequentsData: [],
            transactionRowStatus: {
                new: -1,
                edit: 0,
                saved: 1
            },
            selectedType: null
        };
    },
    mounted() {
        this.updateSubSequentData();
    },
    computed: {
        defaultTransactionInput() {
            const { type, frequency, amount, amountType } = this.$store.getters["transaction/getPreferenceSubSequentSetting"];
            // remove after backend modify the type
            const conversion = {
                0: 12,
                1: 22,
                12: 12,
                22: 22
            };
            return { type: conversion[type], frequency: frequency !== 0 ? frequency : 1, value: amount, unit: amountType };
        },
        fixedFirstCol() {
            return this.elementWidth < 1000;
        },
        tableHeaders() {
            const actionColStyle = this.fixedFirstCol
                ? { width: "100px", position: "sticky", borderLeft: "2px solid #e5e5e5", right: "0px" }
                : { width: "100px" };
            return [
                {
                    fieldName: "name",
                    text: this.getUILabel("portfolio_transactions_column_type"),
                    align: "left",
                    style: {
                        width: "250px"
                    }
                },
                {
                    fieldName: "identifier",
                    align: "left",
                    text: " ",
                    style: {
                        width: "130px"
                    }
                },
                {
                    fieldName: "frequency",
                    align: "left",
                    text: this.getUILabel("portfolio_transactions_column_frequency"),
                    style: {
                        width: "120px"
                    }
                },
                {
                    fieldName: "unit",
                    align: "left",
                    text: this.getUILabel("portfolio_transactions_column_unit"),
                    style: {
                        width: "100px"
                    },
                    transform: unit => {
                        return this.getUnitByAllocateBy(unit);
                    }
                },
                {
                    fieldName: "value",
                    text: this.getUILabel("portfolio_transactions_column_value"),
                    align: "right",
                    style: {
                        width: "100px"
                    },
                    transform: value => {
                        return value ? this.formatter.format(value, "integer") : "";
                    }
                },
                {
                    fieldName: "startDate",
                    text: this.getUILabel("portfolio_transactions_column_start_date"),
                    align: "right",
                    style: {
                        width: "100px"
                    },
                    transform: startDate => {
                        return this.isValidDate(startDate) ? this.formatter.date(new Date(startDate), "mm/yyyy") : "";
                    }
                },
                {
                    fieldName: "endDate",
                    text: this.getUILabel("portfolio_transactions_column_end_date"),
                    align: "right",
                    style: {
                        width: "100px"
                    },
                    transform: endDate => {
                        return this.isValidDate(endDate) ? this.formatter.date(new Date(endDate), "mm/yyyy") : "";
                    }
                },
                {
                    fieldName: "emptyCol",
                    text: " ",
                    style: actionColStyle
                }
            ];
        },
        hypoType() {
            return this.$store.getters["settings/getHypoType"];
        },
        visibleSubSequentsRecord() {
            return this.subSequentsData.filter(item => item.visible);
        },
        validSubSequentsRecord() {
            return this.subSequentsData
                .filter(item => item.status !== -1)
                .map((item, index) => {
                    const isEditMode = item.status === 0;
                    if (isEditMode) {
                        //drop edit draft, use old data.
                        return {
                            type: item.copyData.type,
                            startDate: item.copyData.startDate,
                            endDate: item.copyData.endDate,
                            allocateBy: item.copyData.unit,
                            amount: item.copyData.value,
                            frequency: item.copyData.frequency,
                            visible: item.copyData.visible
                        };
                    }
                    return {
                        type: item.type,
                        startDate: item.startDate,
                        endDate: item.endDate,
                        allocateBy: item.unit,
                        amount: item.value,
                        frequency: item.frequency,
                        visible: item.visible
                    };
                });
        },
        subSequentTypes() {
            return this.$store.getters["transaction/subSequentTypes"];
        },
        frequencies() {
            return this.hypoType === 2
                ? this.$store.getters["transaction/frequencies"].filter(item => item.id !== 99)
                : this.$store.getters["transaction/frequencies"];
        },
        oneTimeTransactionsLimit() {
            const portfolioStartDate = new Date(this.portfolioDate.startDate);
            const portfolioEndDate = new Date(this.portfolioDate.endDate);
            let months;
            months = (portfolioEndDate.getFullYear() - portfolioStartDate.getFullYear()) * 12;
            months -= portfolioStartDate.getMonth();
            months += portfolioEndDate.getMonth();
            if (this.isLastDay(portfolioEndDate)) {
                months += 1;
            }
            return months <= 0 ? 0 : months;
        },
        transactionLimitReached() {
            return this.countTransactions().recurrent >= 5 && this.countTransactions().oneTime >= this.oneTimeTransactionsLimit;
        },
        portfolioAllocateByList() {
            // If Allocated By in Holdings step is Amount or Percentage, this filed shows Amount, Percent
            // If Allocated By is Shares, this field shows Shares, Percent
            let allocateByList;
            if (this.allocateBy === 2) {
                allocateByList = this.allocateByList.filter(item => item.id !== 0);
            } else {
                allocateByList = this.allocateByList.filter(item => item.id !== 2);
            }
            //HYPO-2819, temporarily remove contribution by percentage.
            if (this.selectedType === 12) {
                allocateByList = allocateByList.filter(item => item.id !== 1);
            }
            return allocateByList;
        },
        allocateByList() {
            return this.$store.getters["settings/allocateByList"];
        },
        allocateBy() {
            return this.$store.state.settings.allocateBy;
        },
        holdings() {
            return this.$store.state.holdings.securities;
        },
        portfolioDate() {
            let startDate, endDate;
            this.holdings.forEach(holding => {
                if (!startDate || startDate > new Date(holding.startDate)) {
                    startDate = new Date(holding.startDate);
                }
                if (!endDate || endDate < new Date(holding.endDate)) {
                    endDate = new Date(holding.endDate);
                }
            });
            return {
                startDate,
                endDate
            };
        },
        initContributionRow() {
            const unit = this.allocateBy === 2 ? 2 : 0;
            const total =
                this.allocateBy === 2 ? this.holdings.reduce((sum, item) => sum + item.investShares, 0) : this.$store.state.settings.totalInvestAmount;
            return {
                type: "contribution",
                nameLabelId: "portfolio_transactions_contribution_row_name",
                name: "Contribution",
                frequencyLabelId: "portfolio_transactions_frequency_initial",
                frequency: "Initial",
                unit: unit,
                value: total,
                startDate: this.portfolioDate.startDate,
                endDate: this.portfolioDate.endDate
            };
        },
        contributionHoldings() {
            const columnValueMappping = {
                0: "investAmount",
                1: "investPercent",
                2: "investShares"
            };
            return this.holdings
                .map((item, index) => {
                    return {
                        ...item,
                        type: "holding",
                        frequencyLabelId: "portfolio_transactions_frequency_initial",
                        frequency: "Initial",
                        name: item.securityName,
                        unit: this.allocateBy,
                        value: item[columnValueMappping[this.allocateBy]]
                    };
                })
                .filter(item => item.investPercent > 0);
        }
    },
    watch: {
        portfolioDate: {
            immediate: true,
            handler() {
                const { startDate, endDate } = this.portfolioDate;
                const isPortfolioDateValid = this.isValidDate(startDate) && this.isValidDate(endDate);
                if (isPortfolioDateValid) {
                    this.updateSubSequentData();
                    this.save();
                }
            }
        }
    },
    methods: {
        handleTableScrolled: debounce(function() {
            this.errorToggle && this.$refs.errorTooltip && this.$refs.errorTooltip.updatePosition();
        }, 16),
        toggleContributions() {
            this.isExpandContribution = !this.isExpandContribution;
        },
        isValidDate(date) {
            return isValidDate(new Date(date));
        },
        updateSubSequentData() {
            const updatedData = this.$store.state.transaction.subSequents || this.subSequentsData;
            this.subSequentsData = updatedData.map(item => {
                const isOneTime = item.frequency === 99;
                return {
                    rowId: item.rowId || generateUUID(),
                    type: item.type,
                    nameLabelId: this.getNameLabelIdByType(item.type),
                    frequency: item.frequency,
                    unit: item.allocateBy,
                    value: item.amount,
                    startDate: !isOneTime ? this.getNonOneTimeStartDate() : item.startDate,
                    endDate: !isOneTime ? this.getNonOneTimeEndDate() : item.endDate,
                    status: this.transactionRowStatus["saved"],
                    visible: !isOneTime ? true : isValidTransactionRecord(item.startDate, this.portfolioDate)
                };
            });
        },
        getNameLabelIdByType(typeId) {
            const item = this.subSequentTypes.find(typeItem => typeId === typeItem.typeId);
            return item ? item.labelId : "";
        },
        getFrequencyById(id) {
            const item = this.frequencies.find(frequencyItem => id === frequencyItem.id);
            return item ? this.getUILabel(item.labelId) : "";
        },
        getUnitByAllocateBy(allocateBy) {
            const item = this.allocateByList.find(allocateByItem => allocateBy === allocateByItem.id);
            return item ? this.getUILabel(item.labelId) : "";
        },
        addTransaction() {
            const { type, frequency, unit, value } = this.defaultTransactionInput;
            this.subSequentsData = this.subSequentsData.concat([
                {
                    status: this.transactionRowStatus["new"],
                    rowId: generateUUID(),
                    type,
                    nameLabelId: this.getNameLabelIdByType(type),
                    frequency,
                    unit,
                    value,
                    startDate: this.getNonOneTimeStartDate(),
                    endDate: this.getNonOneTimeEndDate(),
                    visible: true
                }
            ]);
            this.selectedType = type;
        },
        selectType(e, row) {
            const type = Number(e.target.value);
            this.selectedType = type;
            this.subSequentsData = this.subSequentsData.map(item => {
                if (item.rowId === row.rowId) {
                    let unit = row.unit;
                    //contribution
                    if (type === 12) {
                        unit = 0;
                    }
                    return {
                        ...item,
                        type: type,
                        nameLabelId: this.getNameLabelIdByType(type),
                        frequency: item.frequency,
                        unit
                    };
                }
                return item;
            });
        },
        getCurrentMonthEndDateString(dateString) {
            const date = new Date(dateString);
            const monthEndDate = new Date(new Date(date.getFullYear(), date.getMonth() + 1, 1).getTime() - 24 * 60 * 60 * 1000);
            return getDateString(monthEndDate);
        },
        isMonthEndDate(dateString) {
            const monthEndDate = this.getCurrentMonthEndDateString(dateString);
            return new Date(dateString).getDate() === new Date(monthEndDate).getDate();
        },
        getNonOneTimeStartDate() {
            const getNextMonthEndDateString = function(dateString) {
                const date = new Date(dateString);
                const nextMonthEndDate = new Date(new Date(date.getFullYear(), date.getMonth() + 2, 1).getTime() - 24 * 60 * 60 * 1000);
                return getDateString(nextMonthEndDate);
            };
            return this.isMonthEndDate(this.portfolioDate.startDate)
                ? getNextMonthEndDateString(this.portfolioDate.startDate)
                : this.getCurrentMonthEndDateString(this.portfolioDate.startDate);
        },
        getNonOneTimeEndDate() {
            const getLastMonthEndDateString = function(dateString) {
                const date = new Date(dateString);
                const lastMonthEndDate = new Date(new Date(date.getFullYear(), date.getMonth(), 1).getTime() - 24 * 60 * 60 * 1000);
                return getDateString(lastMonthEndDate);
            };
            return this.isMonthEndDate(this.portfolioDate.endDate)
                ? getDateString(this.portfolioDate.endDate)
                : getLastMonthEndDateString(this.portfolioDate.endDate);
        },
        selectFrequency(e, row) {
            //HYPO-2668
            /*When Monthly, Quarterly, Semi-Annually, Annually is selected, Start Date and End Date are non editable month end dates and values as follows:
            Start Date (the first month end date after portfolio start date):
                If Portfolio start date is month end, this Start Date is the next month end after Portfolio start date.
                If Portfolio start date is non month end, this Start Date is the same month end after Portfolio start date.
            End Date (the first month end date on and before portfolio end date):
                If Portfolio end date is month end, this End Date is the same as Portfolio end date.
                If Portfolio end date is non month end, this End Date is the last month end date before Portfolio end date.
            */
            const frequencyId = Number(e.target.value);
            const isOneTime = frequencyId === 99;
            this.subSequentsData = this.subSequentsData.map(item => {
                if (item.rowId === row.rowId) {
                    return {
                        ...item,
                        frequency: frequencyId,
                        unit: item.unit,
                        value: item.value,
                        startDate: isOneTime ? "" : this.getNonOneTimeStartDate(),
                        endDate: isOneTime ? "" : this.getNonOneTimeEndDate()
                    };
                }
                return item;
            });
        },
        selectUnit(e, row) {
            const allocateBy = Number(e.target.value);
            this.subSequentsData = this.subSequentsData.map(item => {
                if (item.rowId === row.rowId) {
                    return {
                        ...item,
                        unit: allocateBy
                    };
                }
                return item;
            });
        },
        changeValue(e, row) {
            const value = Number(e.target.value.replace(",", ""));
            if ((row.unit === 1 && value > 0 && value <= 100) || (row.unit !== 1 && value > 0)) {
                this.subSequentsData = this.subSequentsData.map(item => {
                    if (item.rowId === row.rowId) {
                        return {
                            ...item,
                            value: value
                        };
                    }
                    return item;
                });
            } else {
                e.target.value = e.target._value; //show last start date value
            }
        },
        changeStartDate(inputDate, row, secIndex) {
            this.errorToggle = false;
            this.tooltipTrigger = `${secIndex}sd`;
            const target = this.$refs[this.tooltipTrigger][0].$el;
            const portfolioStartDate = new Date(this.portfolioDate.startDate);
            const portfolioEndDate = new Date(this.portfolioDate.endDate);
            if (inputDate === null || !(inputDate instanceof Date) || isNaN(inputDate)) {
                this.tooltipType = this.dateErrorStatus.invalidDate;
                this.errorToggle = true;
            } else {
                inputDate = this.getMonthEndDate(inputDate); // set to month end
                const isAfterPortfolioStartDate = checkValidDate(inputDate, portfolioStartDate);
                const isBeforePortfolioEndDate = inputDate < portfolioEndDate;
                if (isBeforePortfolioEndDate && isAfterPortfolioStartDate) {
                    //this check if start date for one time transaction is already in use
                    if (this.oneTimeTransactionDateUsed(inputDate, row.rowId)) {
                        this.errorToggle = true;
                        this.tooltipType = this.dateErrorStatus.usedDate;
                    }
                } else {
                    //if the date is not in the range of dates, is either before stardate or after end date
                    if (!isBeforePortfolioEndDate) {
                        this.tooltipType = this.dateErrorStatus.afterEnddate;
                        this.errorToggle = true;
                    } else {
                        this.tooltipType = this.dateErrorStatus.beforeStartdate;
                        this.errorToggle = true;
                    }
                }
            }
            if (!this.errorToggle) {
                this.subSequentsData = this.subSequentsData.map(item => {
                    if (item.rowId === row.rowId) {
                        return {
                            ...item,
                            startDate: getDateString(inputDate)
                        };
                    }
                    return item;
                });
                document.removeEventListener("mousedown", this.blockEvent, true);
                document.removeEventListener("click", this.blockEvent, true);
            } else {
                if (inputDate !== "") {
                    document.addEventListener("click", this.blockEvent, true);
                    document.addEventListener("mousedown", this.blockEvent, true);
                    target.focus();
                } else {
                    this.errorToggle = false;
                    document.removeEventListener("mousedown", this.blockEvent, true);
                    document.removeEventListener("click", this.blockEvent, true);
                }
            }
        },
        getMonthEndDate(inputDate) {
            return new Date(inputDate.getFullYear(), inputDate.getMonth() + 1, 0);
        },
        countTransactions() {
            const countData = { oneTime: 0, recurrent: 0 };
            this.subSequentsData.map(item => {
                if (item.frequency !== "") {
                    if (item.frequency !== 99) {
                        countData.recurrent += 1;
                    } else {
                        countData.oneTime += 1;
                    }
                }
            });
            return countData;
        },
        isFrequencyDisabled(freqType, rowFreqType) {
            if (!freqType && this.countTransactions().recurrent >= 5) {
                return false;
            } else if (freqType !== 99) {
                return rowFreqType !== 99 && rowFreqType !== "" ? false : this.countTransactions().recurrent >= 5;
            } else {
                return rowFreqType === 99 ? false : this.countTransactions().oneTime >= this.oneTimeTransactionsLimit;
            }
        },
        isLastDay(date) {
            const newDate = new Date(date.getTime());
            newDate.setDate(newDate.getDate() + 1);
            return newDate.getDate() === 1;
        },
        oneTimeTransactionDateUsed(date, id) {
            // this method check if the input date for one time transaction is already in use
            const findDate = this.subSequentsData.find(item => {
                return this.formatDateField(item.startDate) === this.formatDateField(date) && item.frequency === 99 && item.rowId !== id;
            });
            return findDate !== undefined;
        },
        formatDateField(date) {
            let newDate = "";
            try {
                newDate = this.formatter.date(new Date(date), "mm/yyyy");
            } catch (error) {
                newDate = date;
            }
            return newDate;
        },
        blockEvent(e) {
            if (e.target.closest(".date-input") === null && e.target.className !== "tooltip-link" && e.target.id !== this.tooltipTrigger) {
                e.stopPropagation();
                e.preventDefault();
            }
        },
        isValidRowData(row) {
            return row.type && row.frequency && typeof row.unit !== "undefined" && row.unit !== "" && row.value && row.startDate;
        },
        handleFirstAction(row) {
            if (row.status === 0) {
                //cancel edit
                this.subSequentsData = this.subSequentsData.map(item => {
                    if (item.rowId === row.rowId) {
                        return {
                            ...item.copyData,
                            status: this.transactionRowStatus["saved"],
                            copyData: undefined
                        };
                    }
                    return item;
                });
            } else if (row.status === -1) {
                // remove new record
                this.subSequentsData = this.subSequentsData.filter(item => item.rowId !== row.rowId);
            } else if (row.status === 1) {
                //remove saved record
                this.$emit("changeNotificationStatus", true);
                this.timeout = setTimeout(() => {
                    this.handleCloseNotification();
                }, 5000);
                this.copySubSequentsData = cloneDeep(this.subSequentsData);
                this.subSequentsData = this.subSequentsData.filter(item => item.rowId !== row.rowId);
                this.save();
            }
        },
        handleSecondAction(row) {
            if (row.status === 1) {
                //edit saved record
                this.subSequentsData = this.subSequentsData.map(item => {
                    return {
                        ...item,
                        status: item.rowId === row.rowId ? this.transactionRowStatus["edit"] : item.status,
                        copyData: { ...item }
                    };
                });
            } else if (row.status === 0) {
                // done
                this.subSequentsData = this.subSequentsData.map(item => {
                    if (item.rowId === row.rowId) {
                        return {
                            ...item,
                            status: this.transactionRowStatus["saved"],
                            copyData: undefined
                        };
                    }
                    return item;
                });
                this.save();
            } else if (row.status === -1) {
                // new record
                this.subSequentsData = this.subSequentsData.map(item => {
                    return {
                        ...item,
                        status: item.rowId === row.rowId ? this.transactionRowStatus["saved"] : item.status
                    };
                });
                this.save();
            }
        },
        save() {
            this.$store.commit(`transaction/${types.SET_SUB_SEQUENTS}`, this.validSubSequentsRecord);
        },
        handleUndo() {
            //restore
            clearTimeout(this.timeout);
            this.subSequentsData = cloneDeep(this.copySubSequentsData);
            this.save();
            this.$emit("changeNotificationStatus", false);
            this.copySubSequentsData = null;
        },
        handleCloseNotification() {
            clearTimeout(this.timeout);
            this.copySubSequentsData = null;
            this.save();
            this.$emit("changeNotificationStatus", false);
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~@mds/typography";
.portfolio-transaction {
    max-width: 1000px;
    .transaction-limit-message {
        font-size: 16px;
        color: #535353;
        line-height: 21px;
        margin-top: 15px;
        display: block;
    }

    &-add-transaction {
        margin-top: $mds-space-1-x;
    }

    &-holdings-table-container.mds-tr--container___hypo {
        > .mds-td___hypo {
            overflow: visible;
            padding-top: 0px;
            padding-bottom: 0px;
        }
        .mds-table___hypo .mds-tbody___hypo .mds-tr___hypo ::v-deep {
            .mds-tr__td--numbered___hypo span {
                visibility: hidden;
            }
            .mds-td___hypo:nth-child(2) {
                padding-left: 20px;
            }
        }
    }
}

.holdings-header {
    font-weight: $mds-typography-font-weight-bold;
}
</style>
